import { BitwayVaultReadAbi, TotalSupplyAbi } from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Bitway Earn reader (BNB — see BITWAY.md) — four calls per entry:
 *   1. `share.totalSupply()`
 *   2. `vault.convertToAssets(1e18, underlying)` → underlying-per-share
 *   3. `vault.getContractBalance(underlying)`    → instant-exit buffer
 *   4. `vault.WAITING_TIME()`                    → live queue wait
 *
 * ## Why not the generic 4626 reader
 *
 * The share token is a bare ERC-20 receipt; the rate lives on the parent
 * MULTI-TOKEN staking vault behind the two-arg
 * `convertToAssets(uint256, address token)`. The vault is carried as
 * `entry.priceOracle` (the hyperbeat-vault convention: "where the rate
 * lives"), and it is also `mintContract` and `inventoryContract` on the
 * same row.
 *
 * ## totalAssets
 *
 * Derived as `totalSupply × rate` — deliberately NOT the vault's
 * `getTVL(token)`: that figure tracks staked PRINCIPAL and only drops at
 * CLAIM time, so it double-counts requested-but-unclaimed exits (whose
 * shares are already burned) and excludes accrued rewards. Share supply
 * times the redeemable rate is what actually backs the holders.
 *
 * ## Liquidity
 *
 * The instant leg (`flashWithdrawWithPenalty`) pays from the staking
 * vault's own token balance — a thin buffer (0.02–1.4% of TVL observed)
 * refilled by ops between custodian sweeps, so 0 is a normal reading.
 * The free `requestClaim` queue (payout FROZEN at request, claimable
 * after `WAITING_TIME`) is the reliable exit.
 *
 * ## The penalty pin
 *
 * `penaltyRate` is PRIVATE with no getter (slot 12), so it cannot be in
 * the multicall: it is pinned per staking vault below — 50 bps on
 * Absolute Return, 0 on Core Alpha, read via raw storage 2026-08-19.
 * Both are admin dials; the live row test re-checks the storage slot.
 */

/** Flash-exit penalty in bps, per staking vault (raw storage slot 12 —
 *  no getter exists). Keyed by the LOWERCASED vault address. */
const BITWAY_PENALTY_BPS: Record<string, number> = {
  // Absolute Return
  '0x5c4a6903732532eeb3ae0803e062d8ae25d52bd1': 50,
  // Core Alpha
  '0xb82e32062c773c7748776c06fdb11b92edae3b63': 0,
}

export const readerBitwayVault = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const { address, underlying, priceOracle } = entry
  const vault = priceOracle ?? entry.mintContract ?? address
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  const penaltyBps = BITWAY_PENALTY_BPS[vault.toLowerCase()]
  return {
    calls: [
      { address, name: 'totalSupply', params: [] },
      {
        address: vault,
        name: 'convertToAssets',
        params: [ONE_E18, underlying],
      },
      { address: vault, name: 'getContractBalance', params: [underlying] },
      { address: vault, name: 'WAITING_TIME', params: [] },
    ],
    abis: [
      TotalSupplyAbi,
      BitwayVaultReadAbi,
      BitwayVaultReadAbi,
      BitwayVaultReadAbi,
    ],
    parse: ([supply, rate, buffer, waitingTime]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (
        totalSupply === undefined ||
        exchangeRate === undefined ||
        exchangeRate === 0n
      ) {
        return undefined
      }
      const capacity = toBigInt(buffer)
      const wait = toBigInt(waitingTime)
      return {
        // totalSupply is in raw share units; convert to raw underlying.
        // (Every live leg is 18/18-dec — BSC stables are 18-dec — but the
        // rescale keeps a future 6-dec listing honest.)
        totalAssets:
          (totalSupply * exchangeRate * underlyingUnit) / (shareUnit * ONE_E18),
        totalSupply,
        exchangeRate,
        withdrawFeeBps: penaltyBps,
        // The flash flag has no getter either (`flashNotEnable`, packed
        // storage — enabled on both vaults live). A drained buffer already
        // zeroes the instant leg's reported liquidity, which is the same
        // caller-visible outcome as a disabled flash path.
        instantRedeemEnabled: penaltyBps !== undefined ? true : undefined,
        instantRedeemCapacity: capacity,
        withdrawalCooldownSeconds:
          wait !== undefined && wait <= 366n * 86400n
            ? Number(wait)
            : undefined,
        withdrawQueue: entry.withdrawQueue,
      }
    },
  }
}
